/**
 * MyDocumentsSection - Caregiver Portal
 *
 * Lets a signed-in caregiver upload personal identity documents
 * (Driver's License, SSN card, auto insurance, CPR/BLS card, TB test,
 * drug screen, nursing license, immunizations). Uploads land in the
 * agency's File Vault with review_status = "pending_review" and
 * uploaded_by_self = true. HR gets a dashboard alert.
 *
 * Mobile-friendly:
 *  - Big "Take Photo" button that opens the rear camera (capture="environment")
 *  - Regular file picker for PDFs
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Camera,
  Upload,
  FileText,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  IdCard,
  Info,
  FormInput,
  Pencil,
} from "lucide-react";
import FillableFormFiller from "./FillableFormFiller";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABEL = {
  driver_license: "Driver's License",
  ssn_card: "Social Security Card",
  auto_insurance: "Auto Insurance",
  cpr_bls: "CPR / BLS Card",
  tb_test: "TB Test",
  drug_screen: "Drug Screen",
  license: "Nursing License",
  immunization: "Immunization Record",
};

const CATEGORIES = Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }));

const formatBytes = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export default function MyDocumentsSection() {
  const [files, setFiles] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [activeForm, setActiveForm] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const [filesRes, formsRes] = await Promise.all([
        axios.get(`${API}/caregiver/me/files`),
        axios.get(`${API}/caregiver/me/fillable-forms`).catch(() => ({ data: { forms: [] } })),
      ]);
      setFiles(filesRes.data.files || []);
      setForms(formsRes.data.forms || []);
    } catch (err) {
      console.error(err);
      // 404 = employee not linked, silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const doUpload = async (file) => {
    if (!file) return;
    if (!category) {
      toast.error("Pick a document type first");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("category", category);
      form.append("description", description || "");
      await axios.post(`${API}/caregiver/me/files`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded — HR will review it shortly.");
      setDescription("");
      // Keep category selected so user can upload another photo (e.g. back of DL)
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
    e.target.value = "";
  };

  const withdraw = async (fileId) => {
    if (!window.confirm("Withdraw this document from review?")) return;
    try {
      await axios.delete(`${API}/caregiver/me/files/${fileId}`);
      toast.success("Withdrawn");
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not withdraw");
    }
  };

  const statusBadge = (f) => {
    switch (f.review_status) {
      case "approved":
        return (
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1">
            <CheckCircle className="w-3 h-3" /> Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100 gap-1">
            <XCircle className="w-3 h-3" /> Rejected
          </Badge>
        );
      default:
        return (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1">
            <Clock className="w-3 h-3" /> Pending Review
          </Badge>
        );
    }
  };

  return (
    <>
    <Card id="section-mydocs" className="scroll-mt-4" data-testid="my-documents-section">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <IdCard className="w-5 h-5 text-blue-600" />
          My Documents
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Upload photos of your driver's license, SSN card, insurance, or
            certifications. HR reviews each upload before it's added to your file.
          </div>
        </div>

        {/* Forms HR has assigned to me — highest priority for the caregiver */}
        {forms.length > 0 && (
          <div className="border-2 border-purple-200 rounded-lg p-3 bg-purple-50/40" data-testid="assigned-forms-section">
            <div className="flex items-center gap-2 mb-2">
              <FormInput className="w-4 h-4 text-purple-700" />
              <h4 className="text-sm font-semibold text-purple-900">
                Forms to Fill ({forms.filter((f) => !f.completed).length} pending)
              </h4>
            </div>
            <ul className="space-y-2">
              {forms.map((f) => (
                <li
                  key={f.id}
                  className="bg-white border border-purple-200 rounded-md p-2.5 flex items-center gap-2"
                  data-testid={`assigned-form-${f.id}`}
                >
                  <FileText className="w-4 h-4 text-purple-500 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-slate-900 truncate">{f.filename}</p>
                    <p className="text-xs text-slate-500">
                      {f.fillable_form?.fields?.length || 0} fields
                      {f.completed && " · Completed"}
                    </p>
                  </div>
                  {f.completed ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1 text-xs">
                      <CheckCircle className="w-3 h-3" /> Done
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => setActiveForm(f)}
                      className="bg-purple-600 hover:bg-purple-700 gap-1 h-8"
                      data-testid={`fill-form-btn-${f.id}`}
                    >
                      <Pencil className="w-3 h-3" /> Fill &amp; Sign
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Uploader form */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-700">Document type *</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="my-docs-category-select">
              <SelectValue placeholder="Pick a document type" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <label className="text-xs font-medium text-slate-700 mt-2 block">
            Note (optional)
          </label>
          <Input
            placeholder="e.g. front side, expires 2027"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="my-docs-description-input"
          />

          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={onFilePicked}
            data-testid="my-docs-camera-input"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={onFilePicked}
            data-testid="my-docs-file-input"
          />

          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 gap-2 h-14 text-base"
              onClick={() => {
                if (!category) return toast.error("Pick a document type first");
                cameraInputRef.current?.click();
              }}
              disabled={uploading}
              data-testid="my-docs-take-photo-btn"
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Camera className="w-5 h-5" />
                  Take Photo
                </>
              )}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="gap-2 h-14 text-base"
              onClick={() => {
                if (!category) return toast.error("Pick a document type first");
                fileInputRef.current?.click();
              }}
              disabled={uploading}
              data-testid="my-docs-upload-file-btn"
            >
              <Upload className="w-5 h-5" />
              Upload File
            </Button>
          </div>
        </div>

        {/* Existing files */}
        <div className="pt-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-slate-800">Your uploads</h4>
            <span className="text-xs text-slate-500">{files.length} total</span>
          </div>
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg text-sm">
              <FileText className="w-6 h-6 mx-auto mb-1 opacity-50" />
              No documents uploaded yet
            </div>
          ) : (
            <ul className="space-y-2">
              {files.map((f) => (
                <li
                  key={f.id}
                  className="bg-white border border-slate-200 rounded-lg p-3"
                  data-testid={`my-doc-item-${f.id}`}
                >
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-slate-900 truncate">
                        {CATEGORY_LABEL[f.category] || f.category}
                      </p>
                      <p className="text-xs text-slate-500 truncate">{f.filename}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        {statusBadge(f)}
                        <span className="text-xs text-slate-400">
                          {formatBytes(f.size)} · {new Date(f.uploaded_at).toLocaleDateString()}
                        </span>
                      </div>
                      {f.review_status === "rejected" && f.rejection_reason && (
                        <p className="text-xs text-red-700 mt-1.5 bg-red-50 border border-red-100 rounded px-2 py-1">
                          <strong>HR note:</strong> {f.rejection_reason}
                        </p>
                      )}
                    </div>
                    {f.review_status !== "approved" && (
                      <button
                        onClick={() => withdraw(f.id)}
                        className="text-slate-400 hover:text-red-600 flex-shrink-0"
                        data-testid={`withdraw-doc-${f.id}`}
                        aria-label="Withdraw"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>

    <FillableFormFiller
      open={!!activeForm}
      onClose={() => setActiveForm(null)}
      form={activeForm}
      onSubmitted={fetchFiles}
    />
    </>
  );
}
